/**
 * SupabaseApiEndpointsRepository
 * 
 * Responsabilidade: Acesso a dados de API endpoints (webhooks incoming) no Supabase
 * Princípios: Single Responsibility, Dependency Inversion
 */

import { supabase } from '../../lib/supabase';
import { ApiEndpoint } from '../../types';
import { createModuleLogger } from '../../utils/logger';

const log = createModuleLogger('[ApiEndpointsRepository]');

export class SupabaseApiEndpointsRepository {
    async findAll(organizationId: string): Promise<ApiEndpoint[]> {
        const { data, error } = await supabase
            .from('api_endpoints')
            .select('*')
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false });

        if (error) {
            // Tabela não existe ainda (42P01) - retorna array vazio
            if (error.code === '42P01' || error.message?.includes('does not exist')) {
                log.warn('Tabela api_endpoints não existe. Execute a migration.');
                return [];
            }
            log.error('Error fetching endpoints:', error);
            throw new Error(`Erro ao buscar endpoints: ${error.message}`);
        }

        return (data || []).map(this.mapToEntity);
    }

    async findById(id: string, organizationId: string): Promise<ApiEndpoint | null> {
        const { data, error } = await supabase
            .from('api_endpoints')
            .select('*')
            .eq('id', id)
            .eq('organization_id', organizationId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            log.error('Error fetching endpoint:', error);
            throw new Error(`Erro ao buscar endpoint: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async findBySlug(slug: string): Promise<{ endpoint: ApiEndpoint; organizationId: string } | null> {
        const { data, error } = await supabase
            .from('api_endpoints')
            .select('*')
            .eq('slug', slug)
            .eq('is_active', true)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            log.error('Error fetching endpoint by slug:', error);
            return null;
        }

        return {
            endpoint: this.mapToEntity(data),
            organizationId: data.organization_id
        };
    }

    async create(
        endpoint: Omit<ApiEndpoint, 'id' | 'createdAt' | 'updatedAt' | 'requestCount' | 'lastUsedAt'>,
        organizationId: string
    ): Promise<ApiEndpoint> {
        if (endpoint.authType !== 'bearer' && endpoint.authType !== 'basic') {
            throw new Error('Autenticação obrigatória: use Bearer ou HTTP Basic');
        }

        const { data, error } = await supabase
            .from('api_endpoints')
            .insert([{
                organization_id: organizationId,
                name: endpoint.name,
                slug: endpoint.slug,
                description: endpoint.description || null,
                auth_type: endpoint.authType,
                auth_token: endpoint.authToken || null,
                basic_username: endpoint.basicUsername || null,
                basic_password: endpoint.basicPassword || null,
                http_methods: endpoint.httpMethods,
                allowed_actions: endpoint.allowedActions,
                is_active: endpoint.isActive !== undefined ? endpoint.isActive : true
            }])
            .select()
            .single();

        if (error) {
            log.error('Error creating endpoint:', error);
            throw new Error(`Erro ao criar endpoint: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async update(id: string, endpoint: Partial<ApiEndpoint>, organizationId: string): Promise<ApiEndpoint> {
        const updateData: Record<string, unknown> = {};

        if (endpoint.name !== undefined) updateData.name = endpoint.name;
        if (endpoint.slug !== undefined) updateData.slug = endpoint.slug;
        if (endpoint.description !== undefined) updateData.description = endpoint.description;
        if (endpoint.authType !== undefined) {
            if (endpoint.authType !== 'bearer' && endpoint.authType !== 'basic') {
                throw new Error('Autenticação obrigatória: use Bearer ou HTTP Basic');
            }
            updateData.auth_type = endpoint.authType;
        }
        if (endpoint.authToken !== undefined) updateData.auth_token = endpoint.authToken;
        if (endpoint.basicUsername !== undefined) updateData.basic_username = endpoint.basicUsername;
        if (endpoint.basicPassword !== undefined) updateData.basic_password = endpoint.basicPassword;
        if (endpoint.httpMethods !== undefined) updateData.http_methods = endpoint.httpMethods;
        if (endpoint.allowedActions !== undefined) updateData.allowed_actions = endpoint.allowedActions;
        if (endpoint.isActive !== undefined) updateData.is_active = endpoint.isActive;

        const { data, error } = await supabase
            .from('api_endpoints')
            .update(updateData)
            .eq('id', id)
            .eq('organization_id', organizationId)
            .select()
            .single();

        if (error) {
            log.error('Error updating endpoint:', error);
            throw new Error(`Erro ao atualizar endpoint: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async delete(id: string, organizationId: string): Promise<void> {
        const { error } = await supabase
            .from('api_endpoints')
            .delete()
            .eq('id', id)
            .eq('organization_id', organizationId);

        if (error) {
            log.error('Error deleting endpoint:', error);
            throw new Error(`Erro ao deletar endpoint: ${error.message}`);
        }
    }

    async incrementRequestCount(id: string): Promise<void> {
        const { error } = await supabase.rpc('increment_api_endpoint_count', { endpoint_id: id });

        if (error) {
            // Fallback para update manual se RPC não existir
            await supabase
                .from('api_endpoints')
                .update({
                    request_count: supabase.rpc('increment', { x: 1 }),
                    last_used_at: new Date().toISOString()
                })
                .eq('id', id);
        }
    }

    async regenerateToken(id: string, organizationId: string): Promise<string> {
        const newToken = this.generateSecureToken();

        await supabase
            .from('api_endpoints')
            .update({ auth_token: newToken })
            .eq('id', id)
            .eq('organization_id', organizationId);

        return newToken;
    }

    private generateSecureToken(): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const tokenLength = 32;
        let token = '';
        const randomValues = new Uint8Array(tokenLength);
        crypto.getRandomValues(randomValues);

        for (let i = 0; i < tokenLength; i++) {
            token += chars[randomValues[i] % chars.length];
        }

        return token;
    }

    private mapToEntity(data: Record<string, unknown>): ApiEndpoint {
        const validMethods = ['GET', 'POST', 'PUT', 'DELETE'] as const;
        const rawMethods = (data.http_methods as string[]) || ['POST'];
        const httpMethods = rawMethods.filter(
            (m): m is 'GET' | 'POST' | 'PUT' | 'DELETE' => validMethods.includes(m as typeof validMethods[number])
        );

        return {
            id: data.id as string,
            name: data.name as string,
            slug: data.slug as string,
            description: data.description as string | undefined,
            authType: data.auth_type === 'basic' ? 'basic' : 'bearer',
            authToken: data.auth_token as string | undefined,
            basicUsername: data.basic_username as string | undefined,
            basicPassword: data.basic_password as string | undefined,
            httpMethods: httpMethods.length > 0 ? httpMethods : ['POST'],
            allowedActions: (data.allowed_actions as string[]) || [],
            isActive: data.is_active as boolean,
            lastUsedAt: data.last_used_at as string | undefined,
            requestCount: data.request_count as number | undefined,
            createdAt: data.created_at as string | undefined,
            updatedAt: data.updated_at as string | undefined
        };
    }
}

export const apiEndpointsRepository = new SupabaseApiEndpointsRepository();
